import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, AlertTriangle, Info, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import i18n from '../i18n/config';
import { cn, formatTime } from '../lib/utils';
import {
  formatNotificationForDisplay,
  getNotificationSeverityLabel,
  getNotificationSourceLabel,
} from '../shared/notifications';
import { useNotificationStore } from '../shared/store/notificationStore';
import { useBusSocket } from '../shared/ws/useBusSocket';
import type { NotificationSource } from '../shared/store/notificationStore';

function parsePayload(payload: unknown): {
  severity: 'critical' | 'warning' | 'info';
  source: NotificationSource;
  title: string;
  message: string;
  ts: number;
  dedupKey: string;
  link: string;
} | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  if (String(p.type) !== 'notification') return null;
  const rawType = String(p.notification_type || 'info').toLowerCase();
  const severity = rawType === 'error' ? 'critical' : rawType === 'warning' ? 'warning' : 'info';
  const sourceRaw = String(p.source || 'system');
  const source: NotificationSource = (
    ['system', 'health', 'build', 'backfill', 'signal', 'execution', 'user-action'].includes(sourceRaw)
      ? sourceRaw
      : 'system'
  ) as NotificationSource;
  const title =
    typeof p.title === 'string' && p.title.trim()
      ? p.title.trim()
      : i18n.t('notifications:defaults.untitled');
  const message =
    typeof p.message === 'string' && p.message.trim()
      ? p.message.trim()
      : i18n.t('notifications:defaults.noDescription');
  const ts = typeof p.timestamp_ms === 'number' ? p.timestamp_ms : Date.now();
  return {
    severity,
    source,
    title,
    message,
    ts,
    dedupKey:
      typeof p.dedup_key === 'string' && p.dedup_key.trim()
        ? p.dedup_key.trim()
        : `${source}:${title}:${message}`,
    link: typeof p.link === 'string' && p.link.trim() ? p.link.trim() : '/statistics',
  };
}

export const NotificationCenter: FC = () => {
  const { t } = useTranslation(['common', 'notifications']);
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'critical' | 'warning' | 'info'>('all');
  const notifications = useNotificationStore((s) => s.items);
  const push = useNotificationStore((s) => s.push);
  const markAsRead = useNotificationStore((s) => s.markRead);
  const markAllAsRead = useNotificationStore((s) => s.markAllRead);
  const removeNotification = useNotificationStore((s) => s.remove);
  const clearAll = useNotificationStore((s) => s.clear);
  const { subscribe } = useBusSocket();

  useEffect(() => {
    const unsub = subscribe('system:notifications', (payload: unknown) => {
      try {
        const n = parsePayload(payload);
        if (n) {
          push(n);
        }
      } catch (e) {
        if (typeof console !== 'undefined' && console.error) {
          console.error('[NotificationCenter] Failed to process WS notification', e);
        }
      }
    });
    return unsub;
  }, [push, subscribe]);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const severityCounts = useMemo(() => {
    const unread = notifications.filter((n) => !n.read);
    return {
      critical: unread.filter((n) => n.severity === 'critical').length,
      warning: unread.filter((n) => n.severity === 'warning').length,
      info: unread.filter((n) => n.severity === 'info').length,
    };
  }, [notifications]);
  const visibleNotifications = useMemo(
    () => (filter === 'all' ? notifications : notifications.filter((n) => n.severity === filter)),
    [filter, notifications],
  );

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent | PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      closePanel();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePanel();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closePanel, isOpen]);

  const getIcon = (severity: 'critical' | 'warning' | 'info') => {
    switch (severity) {
      case 'critical':
        return <X className="text-red-500" size={16} />;
      case 'warning':
        return <AlertTriangle className="text-yellow-500" size={16} />;
      default:
        return <Info className="text-blue-500" size={16} />;
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="relative p-2 hover:bg-accent rounded-md transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={t('notifications:center.openButtonLabel')}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            className="absolute right-0 top-full z-[120] mt-2 flex max-h-[calc(100vh-5rem)] w-[min(24rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] flex-col rounded-lg border border-border bg-card shadow-2xl"
            role="dialog"
            aria-label={t('notifications:center.dialogLabel')}
          >
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{t('notifications:center.title')}</h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={markAllAsRead}
                      className="text-xs text-primary hover:underline"
                    >
                      {t('common:actions.readAll')}
                    </button>
                  )}
                  {notifications.length > 0 && (
                    <button
                      type="button"
                      onClick={clearAll}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      {t('common:actions.clear')}
                    </button>
                  )}
                </div>
              </div>
              {unreadCount > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {[
                    severityCounts.critical > 0 ? t('notifications:center.summary.critical', { count: severityCounts.critical }) : null,
                    severityCounts.warning > 0 ? t('notifications:center.summary.warning', { count: severityCounts.warning }) : null,
                    severityCounts.info > 0 ? t('notifications:center.summary.info', { count: severityCounts.info }) : null,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
            </div>
            <div className="px-4 pt-2 pb-3 flex items-center gap-2 border-b border-border">
              <button
                type="button"
                key="all"
                onClick={() => setFilter('all')}
                className={cn(
                  'text-xs px-2 py-1 rounded-md border',
                  filter === 'all' ? 'bg-accent border-border' : 'border-transparent hover:border-border',
                )}
              >
                {t('common:filters.all')}
              </button>
              {(['critical', 'warning', 'info'] as const).map((f) => (
                <button
                  type="button"
                  key={f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    'text-xs px-2 py-1 rounded-md border',
                    filter === f ? 'bg-accent border-border' : 'border-transparent hover:border-border',
                  )}
                >
                  {getNotificationSeverityLabel(f)}
                </button>
              ))}
            </div>

            <div className="min-h-0 flex-1 max-h-96 overflow-y-auto">
              {visibleNotifications.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Bell className="mx-auto mb-2 opacity-50" size={32} />
                  <p className="text-sm">{t('notifications:center.empty')}</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {visibleNotifications.map((notification) => {
                    const { title: displayTitle, message: displayMessage } = formatNotificationForDisplay(
                      notification.title,
                      notification.message,
                    );
                    return (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className={cn(
                        'p-4 hover:bg-accent/50 transition-colors cursor-pointer group',
                        !notification.read && 'bg-accent/20',
                      )}
                      onClick={() => {
                        markAsRead(notification.id);
                        if (notification.link) {
                          closePanel();
                          navigate(notification.link);
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {getIcon(notification.severity)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-sm">
                                {displayTitle}
                                {(notification.count || 1) > 1 && (
                                  <span className="ml-2 text-xs text-muted-foreground">
                                    ×{notification.count}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {displayMessage}
                              </div>
                              <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                                <span>{formatTime(new Date(notification.ts))}</span>
                                <span className="opacity-70">
                                  {getNotificationSourceLabel(notification.source)}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeNotification(notification.id);
                              }}
                              className="ml-2 p-1 hover:bg-accent rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-3 border-t border-border">
              <button
                type="button"
                onClick={() => {
                  closePanel();
                  navigate('/notifications');
                }}
                className="w-full text-xs text-center text-primary hover:underline inline-flex items-center justify-center gap-1"
              >
                {t('notifications:center.viewAll')}
                <ExternalLink size={12} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
